// API Client mit automatischem Authorization Header
// Ersetzt alle manuellen fetch() Calls

import { API_URL } from '../config';
import { get as idbGet, set as idbSet, del as idbDel, createStore } from 'idb-keyval';

/**
 * Get token — Impersonation-Tab nutzt sessionStorage, sonst localStorage.
 * So bleibt die Admin-Session im Original-Tab unangetastet.
 */
export const getToken = () => {
  return sessionStorage.getItem('snapshot_token') || localStorage.getItem('snapshot_token');
};

// ─────────────────────────────────────────────────────────────
// ETag-Cache + IndexedDB-Persistenz
// ─────────────────────────────────────────────────────────────
// Strategie: Für GETs schickt der Client automatisch If-None-Match mit, wenn
// schon eine Response für diese URL im Cache liegt. Bei 304 liefern wir den
// gecachten Body — ohne Body-Download. Persistiert in IndexedDB, damit der
// Cache auch einen Reload überlebt und Galerien sofort rendern.
//
// Aktiv nur für Requests, die per `options.cache = true` opt-in machen oder
// deren Pfad auf einer Whitelist steht (Polling + Galerien).

const _etagStore = createStore('snapshot-cache', 'etag-v1');

// In-Memory-Mirror für Sync-Zugriff während eines Requests (IDB ist async).
const _memCache = new Map(); // url -> {etag, body, ts}

/** IndexedDB → In-Memory rehydriert einmalig beim App-Start. */
export async function rehydrateApiCache() {
  try {
    const { entries } = await import('idb-keyval');
    const all = await entries(_etagStore);
    for (const [k, v] of all) {
      if (v && typeof v === 'object' && 'etag' in v) {
        _memCache.set(k, v);
      }
    }
    // eslint-disable-next-line no-console
    console.info(`[apiClient] rehydrated ${all.length} cache entries from IDB`);
  } catch (e) {
    console.warn('[apiClient] rehydrate failed:', e);
  }
}

const _DEFAULT_CACHE_PATHS = [
  '/api/user/credits',
  '/api/user/me',
  '/api/auth/daily-claim-status',
  '/api/training/list',
  '/api/generations/list',
  '/api/videos/list',
  '/api/lipsync/list',
  '/api/assemblies/list',
  '/api/pricing/settings/public',
  '/api/pricing/price-list/public',
  '/api/admin/astria-tuning/public/dropdowns',
  '/api/admin/modes/public/',
  '/api/admin/cinema/public/genres',
  '/api/referral/texts/public',
  '/api/referral/bonus-info',
  '/api/legal-texts/',
  '/api/onboarding/config',
];

function _shouldCache(endpoint, opts) {
  if (opts && opts.cache === false) return false;
  if (opts && opts.cache === true) return true;
  return _DEFAULT_CACHE_PATHS.some((p) => endpoint.startsWith(p));
}

function _cacheKey(endpoint) {
  return endpoint; // URL-relativ, User-Dimension steckt im JWT → nicht Key-relevant, weil Browser pro Tab separiert
}

async function _readCache(endpoint) {
  const key = _cacheKey(endpoint);
  const mem = _memCache.get(key);
  if (mem) return mem;
  try {
    const disk = await idbGet(key, _etagStore);
    if (disk) _memCache.set(key, disk);
    return disk;
  } catch {
    return null;
  }
}

async function _writeCache(endpoint, etag, body) {
  const key = _cacheKey(endpoint);
  const entry = { etag, body, ts: Date.now() };
  _memCache.set(key, entry);
  try {
    await idbSet(key, entry, _etagStore);
  } catch {
    /* quota exceeded o. ä. — nur Memory-Cache bleibt */
  }
}

async function _invalidateCache(endpoint) {
  const key = _cacheKey(endpoint);
  _memCache.delete(key);
  try {
    await idbDel(key, _etagStore);
  } catch {
    /* ignore */
  }
}

/**
 * Letzte bekannte Payload für eine URL (aus Memory/IDB). Für „instant render"
 * in Galerien: beim Mount sofort rendern, dann im Hintergrund revalidaten.
 */
export async function peekCached(endpoint) {
  const entry = await _readCache(endpoint);
  return entry ? entry.body : null;
}

/**
 * Gezielte Cache-Invalidation von außen (z. B. nach optimistischen Updates).
 */
export async function invalidateCache(endpoint) {
  await _invalidateCache(endpoint);
}

/**
 * Write-Action → welche Listen-Caches müssen neu geholt werden?
 * Minimalistisch: alles, was die gleiche Ressource betrifft.
 */
function _invalidateRelatedCaches(endpoint) {
  const invalidateStartingWith = (prefix) => {
    const toDel = [];
    for (const key of _memCache.keys()) {
      if (key.startsWith(prefix)) toDel.push(key);
    }
    toDel.forEach((k) => _invalidateCache(k));
  };

  if (endpoint.includes('/api/generation') || endpoint.includes('/api/image') || endpoint.includes('/api/remix')) {
    invalidateStartingWith('/api/generations/list');
  }
  if (endpoint.includes('/api/video') || endpoint.includes('/api/animate') || endpoint.includes('/api/beatleap')) {
    invalidateStartingWith('/api/videos/list');
    invalidateStartingWith('/api/assemblies/list');
  }
  if (endpoint.includes('/api/lipsync')) {
    invalidateStartingWith('/api/lipsync/list');
  }
  if (endpoint.includes('/api/training') || endpoint.includes('/api/avatar')) {
    invalidateStartingWith('/api/training/list');
  }
  if (endpoint.includes('/api/stripe') || endpoint.includes('/api/snaps') || endpoint.includes('/api/credits')) {
    _invalidateCache('/api/user/credits');
    _invalidateCache('/api/user/me');
  }
  if (endpoint.includes('/api/auth/daily-claim')) {
    _invalidateCache('/api/auth/daily-claim-status');
    _invalidateCache('/api/user/credits');
    _invalidateCache('/api/user/me');
  }
  if (endpoint.includes('/api/onboarding')) {
    _invalidateCache('/api/onboarding/config');
  }
}

/**
 * Handle 401 - Nur redirecten wenn User eingeloggt WAR
 */
const handle401 = (hadToken) => {
  if (hadToken) {
    console.log('🚨 Token invalidiert - Automatischer Logout');
    // Impersonation-Tab? → Nur sessionStorage leeren, nicht localStorage (Admin-Session schützen)
    if (sessionStorage.getItem('snapshot_impersonation')) {
      sessionStorage.clear();
      window.close();
      return;
    }
    localStorage.removeItem('snapshot_token');
    localStorage.removeItem('snapshot_user');
    localStorage.removeItem('snapshot_beta_access');
    localStorage.removeItem('snapshotpro_generating');
    window.location.href = '/welcome';
  } else {
    console.log('ℹ️ 401 ohne Token - kein Redirect (öffentliche Seite)');
  }
};

/**
 * API Client - Automatisch mit Authorization Header
 */
export const apiClient = {
  
  /**
   * GET Request — mit automatischem Retry bei Netzwerk-Fehlern (3x, exponentiell)
   * und transparentem ETag-Revalidate + IndexedDB-Persistenz für Polling-Endpoints.
   *
   * Bei 304 Not Modified liefert die Funktion ein Response-like-Objekt, dessen
   * .json() die gecachte Payload zurückgibt. Aus Sicht des aufrufenden Codes
   * verhält es sich wie ein 200 OK — nur ohne Body-Download.
   */
  get: async (endpoint, options = {}) => {
    const token = getToken();
    const hadToken = !!token;
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // ETag-Revalidate nur für Whitelist-Endpoints (oder explizites opt-in)
    const useCache = _shouldCache(endpoint, options);
    let cacheEntry = null;
    if (useCache) {
      cacheEntry = await _readCache(endpoint);
      if (cacheEntry && cacheEntry.etag) {
        headers['If-None-Match'] = cacheEntry.etag;
      }
    }

    const maxRetries = options.retries ?? 3;
    let lastError;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${API_URL}${endpoint}`, {
          method: 'GET',
          headers
        });

        if (response.status === 401) {
          handle401(hadToken);
          return response;
        }

        if (response.status === 304 && cacheEntry) {
          // Cached Payload als Response-Proxy zurückgeben
          return {
            ok: true,
            status: 200,
            statusText: 'OK (from cache)',
            headers: response.headers,
            _fromCache: true,
            json: async () => cacheEntry.body,
            text: async () => JSON.stringify(cacheEntry.body),
          };
        }

        if (response.status >= 500 && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 300 * Math.pow(2, attempt)));
          continue;
        }

        // 200er → bei Whitelist in ETag-Cache legen
        if (useCache && response.ok && response.status === 200) {
          const etag = response.headers.get('etag');
          if (etag) {
            // Body klonen, damit der aufrufende Code ihn auch lesen kann
            const cloned = response.clone();
            try {
              const body = await cloned.json();
              await _writeCache(endpoint, etag, body);
            } catch {
              /* kein JSON → nicht cachen */
            }
          } else {
            // Endpoint ohne ETag: trotzdem Body persistieren für peekCached()
            try {
              const cloned = response.clone();
              const body = await cloned.json();
              await _writeCache(endpoint, null, body);
            } catch { /* ignore */ }
          }
        }

        return response;
      } catch (err) {
        lastError = err;
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 300 * Math.pow(2, attempt)));
          continue;
        }
        throw lastError;
      }
    }
  },
  
  /**
   * POST Request
   */
  post: async (endpoint, data = null, options = {}) => {
    const token = getToken();
    const hadToken = !!token;
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Write-Action invalidiert verknüpfte Gallery-Caches
    _invalidateRelatedCaches(endpoint);
    
    // Timeout: 180s für Video-Requests, Bild-Generierung und Remix, 60s Standard
    const isLongRequest = endpoint.includes('video') || endpoint.includes('generate') || endpoint.includes('remix');
    const timeoutMs = options.timeout || (isLongRequest ? 180000 : 60000);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // ✅ Globaler 401-Handler - nur wenn Token existierte
      if (response.status === 401) {
        handle401(hadToken);
        return response;
      }
      
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error('Request timeout - Video-Generierung dauert länger als erwartet');
      }
      throw err;
    }
  },
  
  /**
   * DELETE Request
   */
  delete: async (endpoint, data = null) => {
    const token = getToken();
    const hadToken = !!token;
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Write-Action invalidiert verknüpfte Gallery-Caches
    _invalidateRelatedCaches(endpoint);
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
      body: data ? JSON.stringify(data) : undefined
    });
    
    // ✅ Globaler 401-Handler - nur wenn Token existierte
    if (response.status === 401) {
      handle401(hadToken);
      return response;
    }
    
    return response;
  }
  
};
